import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Delete, ShieldCheck, ChevronLeft, Users, Phone, Fingerprint } from "lucide-react";
import { passkeyOtpFallback, unlockOtpCall } from "../lib/api";
import { toast, Toaster } from "sonner";
import { useSecurity } from "../context/SecurityContext";
import { ForgotPinForm } from "./ForgotPinForm";
import { AdminEmailStep } from "./AdminEmailStep";
import { BrandMark } from "./BrandMark";
import { GoogleIdentity } from "./GoogleAuth";
import { tr } from "../lib/i18n";
import { LangToggle } from "./LangToggle";
import { isNativeApp } from "../lib/native";

const PAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "blank", "0", "del"];
const DOTS = ["d1", "d2", "d3", "d4", "d5", "d6"];
const ROLE_HINT = { owner: "Full access", partner: "Can add & edit", executor: "Read-only", health_agent: "Medical papers only", finance_agent: "Financial papers only" };
const NEXT_HINT = { health_agent: "Enter your PIN, then verify your ID once. The healthcare POA, living will, DNR / POLST and HIPAA release open right after.", finance_agent: "Enter your PIN, then verify your ID once. The financial POA and money papers open right after, read-only.", executor: "You'll enter your PIN, then you can request access. The owner is told and can decline during the waiting period; medical directives open early only if no separate healthcare agent is named.", partner: "Enter your PIN to open the shared vault.", owner: "Enter your PIN or use your passkey." };

// Pre-unlock accessibility: larger text persists in the same hv-a11y settings the in-app panel uses.
const LargeTextToggle = () => {
  const stored = () => { try { return JSON.parse(localStorage.getItem("hv-a11y") || "{}"); } catch { return {}; } };
  const [on, setOn] = useState(() => (stored().scale || 100) > 100);
  const toggle = () => {
    const s = stored();
    const scale = on ? 100 : Math.max(125, s.scale || 100); // keep a larger size chosen in the accessibility panel
    document.documentElement.style.fontSize = scale === 100 ? "" : `${scale}%`;
    try { s.scale = scale; localStorage.setItem("hv-a11y", JSON.stringify(s)); } catch { /* ignore */ }
    setOn(!on);
  };
  useEffect(() => { const sc = stored().scale || 100; if (sc > 100) document.documentElement.style.fontSize = `${sc}%`; }, []);
  return (
    <button type="button" onClick={toggle} aria-pressed={on} data-testid="lock-large-text" className={`fixed bottom-4 right-4 z-30 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs shadow-sm transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}>
      <span className="font-serif text-base leading-none">A</span><span className="font-serif text-xs leading-none">A</span> {tr(on ? "Normal text" : "Larger text")}
    </button>
  );
};

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

// Initials are drawn via CSS (::after) so they never become part of the surrounding text for screen readers or copy/paste.
export const MemberAvatar = ({ member, size = "h-10 w-10 text-sm", className = "" }) => (
  <span
    data-testid="member-avatar"
    role="img"
    aria-label={member?.name ? `${member.name}'s avatar` : "avatar"}
    data-initials={initials(member?.name)}
    className={`${size} rounded-full grid place-items-center font-semibold text-white shrink-0 after:content-[attr(data-initials)] ${className}`}
    style={{ backgroundColor: member?.color || "#C25E30" }}
  />
);

const MemberPicker = ({ members, onPick }) => (
  <motion.div key="pick" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}>
    <h1 className="mt-5 font-serif text-2xl font-medium">{tr("Who's here?")}</h1>
    <p className="mt-1 text-sm text-muted-foreground flex items-center justify-center gap-1.5">
      <Users className="h-3.5 w-3.5" /> {tr("Choose your profile to unlock")}
    </p>
    <div data-testid="member-picker" className="mt-7 grid grid-cols-2 gap-3">
      {members.map((m, i) => (
        <motion.button
          key={m.id}
          data-testid={`member-pick-${m.id}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
          onClick={() => onPick(m)}
          className="rounded-2xl border border-border bg-card p-4 text-left hover:border-primary hover:-translate-y-0.5 transition-all"
        >
          <MemberAvatar member={m} />
          <div className="mt-3 font-medium leading-tight truncate">{m.name}</div>
          <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground mt-0.5">
            {tr(m.role_label)}{ROLE_HINT[m.role] ? ` · ${tr(ROLE_HINT[m.role])}` : ""}
          </div>
        </motion.button>
      ))}
    </div>
  </motion.div>
);

const usePinEntry = (onSubmit) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const pinRef = useRef(pin);
  pinRef.current = pin;

  const submit = async (value) => {
    setBusy(true);
    try {
      await onSubmit(value);
    } catch (e) {
      setError(true);
      pinRef.current = "";
      setPin("");
      toast.error(e?.response?.data?.detail || "Incorrect PIN");
      setTimeout(() => setError(false), 600);
    } finally {
      setBusy(false);
    }
  };

  const press = (key) => {
    if (busy || key === "blank") return;
    if (key === "del") { const next = pinRef.current.slice(0, -1); pinRef.current = next; return setPin(next); }
    const next = (pinRef.current + key).slice(0, 6);
    pinRef.current = next;
    setPin(next);
    if (next.length === 6) submit(next);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); press(e.key); }
      else if (e.key === "Backspace") { e.preventDefault(); press("del"); }
      else if (e.key === "Enter" && pinRef.current.length >= 4 && pinRef.current.length < 6) { e.preventDefault(); submit(pinRef.current); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  return { pin, error, busy, press, submit, reset: () => { pinRef.current = ""; setPin(""); } };
};

const PasskeyStep = ({ pending, name, otpAvailable, onBack, onPasskey, onOtp }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const running = useRef(false);
  const go = async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true); setErr("");
    try { await onPasskey(pending); } catch (e) { setErr(e?.name === "NotAllowedError" ? "Cancelled or timed out — try again." : e?.response?.data?.detail || e?.message || "Passkey check failed"); } finally { setBusy(false); running.current = false; }
  };
  useEffect(() => { const t = setTimeout(go, 150); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  return (
    <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} data-testid="passkey-step" className="mt-6">
      <div className="text-sm font-medium">{tr("Second step — confirm it's")} {name || "you"}</div>
      <p className="text-xs text-muted-foreground mt-1">{tr("Use Face ID, Touch ID, Windows Hello or your security key.")}</p>
      {err && <p className="mt-3 text-xs text-destructive" data-testid="passkey-error">{err}</p>}
      <button data-testid="passkey-use-btn" disabled={busy} onClick={go} className="mt-4 w-full h-11 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">{busy ? "Waiting for your device…" : "Use passkey"}</button>
      {otpAvailable && <button data-testid="passkey-otp-fallback-btn" disabled={busy} onClick={onOtp} className="mt-3 text-xs text-muted-foreground hover:text-primary block mx-auto">{tr("Email me a code instead")}</button>}
      <button data-testid="passkey-back-btn" onClick={onBack} className="mt-3 text-xs text-muted-foreground hover:text-primary">{tr("← Back to PIN")}</button>
    </motion.div>
  );
};

const OtpStep = ({ hint, onBack, onSubmit, challengeId, canCall }) => {
  const [calling, setCalling] = useState(false);
  const callMe = async () => {
    setCalling(true);
    try { const r = await unlockOtpCall(challengeId); toast.success(r.lang === "es" ? "Le estamos llamando con su código" : "Calling you with your code now"); }
    catch (e) { toast.error(e?.response?.data?.detail || tr("Couldn't place the call")); }
    finally { setCalling(false); }
  };
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onSubmit(code); } catch (e) { toast.error(e?.response?.data?.detail || "That code isn't right"); setCode(""); } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} data-testid="otp-step" className="mt-6">
      <div className="text-sm font-medium">{tr("Second step — check your email")}</div>
      <p className="text-xs text-muted-foreground mt-1">{tr("We sent a 6-digit code to")} <b>{hint}</b>{tr(". It expires in 10 minutes.")}</p>
      <input data-testid="otp-input" autoFocus inputMode="numeric" value={code} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setCode(v); }} onKeyDown={(e) => e.key === "Enter" && code.length === 6 && go()} placeholder="••••••" className="mt-4 w-full h-12 rounded-xl border border-border bg-card text-center font-mono text-2xl tracking-[0.5em]" />
      <button data-testid="otp-submit-btn" disabled={busy || code.length !== 6} onClick={go} className="mt-3 w-full h-11 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">{busy ? "Checking…" : "Unlock"}</button>
      <div className="mt-3 flex items-center justify-center gap-4 text-xs">
        <button data-testid="otp-back-btn" onClick={onBack} className="text-muted-foreground hover:text-primary">{tr("← Back to PIN")}</button>
        {canCall && <button data-testid="otp-call-btn" onClick={callMe} disabled={calling} className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-60"><Phone className="h-3 w-3" /> {calling ? tr("Calling…") : tr("Read it to me by phone")}</button>}
      </div>
    </motion.div>
  );
};

const PinPad = ({ selected, showSwitch, onSwitch, onSubmit, onForgot }) => {
  const { pin, error, busy, press, submit, reset } = usePinEntry(onSubmit);
  return (
    <motion.div key="pin" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}>
      {showSwitch ? (
        <div className="mt-5 flex items-center justify-center gap-3">
          <MemberAvatar member={selected} />
          <div className="text-left">
            <h1 className="font-serif text-2xl font-medium leading-none" data-testid="lock-member-name">
              {selected.name}
            </h1>
            {NEXT_HINT[selected.role] && <p className="mt-1.5 text-xs text-muted-foreground max-w-xs leading-snug" data-testid="lock-next-hint">{tr(NEXT_HINT[selected.role])}</p>}
            <button
              data-testid="switch-member-btn"
              onClick={() => {
                reset();
                onSwitch();
              }}
              className="mt-1 text-xs text-primary inline-flex items-center gap-0.5 hover:underline"
            >
              <ChevronLeft className="h-3 w-3" /> {tr("Not you?")}
            </button>
          </div>
        </div>
      ) : (
        <h1 className="mt-5 font-serif text-2xl font-medium">{tr("Vault locked")}</h1>
      )}
      <p className="mt-2 text-sm text-muted-foreground flex items-center justify-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5" /> {tr("Enter your PIN to continue")}
      </p>

      <motion.div animate={error ? { x: [0, -8, 8, -6, 6, 0] } : {}} transition={{ duration: 0.4 }} className="mt-7 flex items-center justify-center gap-3">
        {DOTS.map((id, i) => (
          <span
            key={id}
            className={`h-3.5 w-3.5 rounded-full transition-colors ${i < pin.length ? "bg-primary" : "bg-border"} ${error ? "bg-destructive" : ""}`}
          />
        ))}
      </motion.div>

      <div className="mt-8 grid grid-cols-3 gap-3 max-w-xs mx-auto">
        {PAD.map((key) => (
          <button
            key={key}
            data-testid={key === "del" ? "pin-del" : key === "blank" ? "pin-empty" : `pin-${key}`}
            onClick={() => press(key)}
            disabled={key === "blank"}
            className={`h-16 rounded-2xl text-xl font-medium transition-all ${
              key === "blank" ? "invisible" : "bg-card border border-border hover:bg-accent active:scale-95 hover:-translate-y-0.5"
            }`}
          >
            {key === "del" ? <Delete className="h-5 w-5 mx-auto" /> : key}
          </button>
        ))}
      </div>
      {pin.length >= 4 && pin.length < 6 && (
        <button data-testid="pin-submit-btn" onClick={() => submit(pin)} disabled={busy} className="mt-5 text-sm font-medium text-primary hover:underline">
          {tr("Unlock with")} {pin.length}-digit PIN
        </button>
      )}
      <div className="mt-6">
        <button data-testid="forgot-pin-btn" onClick={onForgot} className="text-xs text-muted-foreground hover:text-primary hover:underline">
          {tr("Forgot the PIN?")}
        </button>
      </div>
    </motion.div>
  );
};

const LAST_KEY = "hv_last_member";

export const LockScreen = () => {
  const { doUnlock, doUnlockOtp, doUnlockPasskey, doUnlockAdminPasskey, finishUnlock, status } = useSecurity();
  const [adminPasskeyBusy, setAdminPasskeyBusy] = useState(false);
  const adminPasskey = async () => {
    setAdminPasskeyBusy(true);
    try { await doUnlockAdminPasskey(); }
    catch (e) { if (e?.name !== "NotAllowedError") toast.error(e?.response?.data?.detail || "Passkey sign-in didn't complete"); }
    finally { setAdminPasskeyBusy(false); }
  };
  const members = status?.members || [];
  const [picked, setPicked] = useState(null);
  const [switched, setSwitched] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("household") === "gone") {
      toast.info("That vault link is no longer active — you're back at your own vault.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  const [forgot, setForgot] = useState(false);
  const [adminEmail, setAdminEmail] = useState(false);
  const [otp, setOtp] = useState(null);
  const [passkey, setPasskey] = useState(null);
  const remembered = !switched && members.find((m) => m.id === localStorage.getItem(LAST_KEY));
  const selected = members.length === 1 ? members[0] : picked || remembered || null;
  const choosing = members.length > 1 && !selected;

  const unlock = async (pin) => {
    const res = await doUnlock(pin, selected?.id || null);
    if (selected?.id) localStorage.setItem(LAST_KEY, selected.id);
    if (res?.requires_passkey) setPasskey(res);
    else if (res?.requires_otp) setOtp(res);
  };
  const otpInstead = async () => {
    try { const r = await passkeyOtpFallback(passkey.pending); setPasskey(null); setOtp(r); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send a code"); }
  };

  return (
    <div
      data-testid="lock-screen"
      className="fixed inset-0 z-[100] bg-background grid place-items-center px-6"
      style={{
        backgroundImage: "radial-gradient(hsl(var(--foreground) / 0.03) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      <LargeTextToggle />
      <Toaster position="top-center" richColors />
      <div className="absolute top-4 right-4"><LangToggle /></div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm text-center">
        <BrandMark size="lg" />
        <AnimatePresence mode="wait">
          {status?.awaiting_owner ? (
            <motion.div key="await" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="awaiting-owner" className="mt-6">
              <h1 className="font-serif text-2xl font-medium">{tr("This vault is waiting for its owner")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{tr("Your advisor set it up for you. Open the link in your welcome email to choose your PIN and step inside.")}</p>
            </motion.div>
          ) : passkey ? (
            <PasskeyStep key="passkey" pending={passkey.pending} name={passkey.member_name} otpAvailable={passkey.otp_available} onBack={() => setPasskey(null)} onPasskey={doUnlockPasskey} onOtp={otpInstead} />
          ) : otp ? (
            <OtpStep key="otp" hint={otp.email_hint} challengeId={otp.challenge_id} canCall={!!otp.can_call} onBack={() => setOtp(null)} onSubmit={(code) => doUnlockOtp(otp.challenge_id, code)} />
          ) : forgot ? (
            <ForgotPinForm onBack={() => setForgot(false)} />
          ) : adminEmail ? (
            <AdminEmailStep key="admin-email" onBack={() => setAdminEmail(false)} onSent={(r) => { setAdminEmail(false); setOtp(r); }} />
          ) : choosing ? (
            <MemberPicker members={members} onPick={setPicked} />
          ) : (
            <PinPad
              selected={selected}
              showSwitch={Boolean(selected && members.length > 1)}
              onSwitch={() => {
                setPicked(null);
                setSwitched(true);
                localStorage.removeItem(LAST_KEY);
              }}
              onSubmit={unlock}
              onForgot={() => setForgot(true)}
            />
          )}
        </AnimatePresence>
        {!status?.awaiting_owner && !otp && !passkey && !forgot && !adminEmail && selected && members.length > 1 && (
          <div className="mt-4" data-testid="quick-switch">
            <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{tr("Or sign in as")}</div>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {members.filter((m) => m.id !== selected.id).map((m) => (
                <button key={m.id} type="button" data-testid={`quick-switch-${m.id}`} onClick={() => { setPicked(m); setSwitched(true); }} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card pl-1 pr-3 py-1 text-xs hover:border-primary transition-colors">
                  <MemberAvatar member={m} size="h-6 w-6 text-[10px]" /> {m.name}{m.role === "admin" ? <span className="text-[9px] font-mono uppercase text-primary">admin</span> : null}
                </button>
              ))}
            </div>
          </div>
        )}
        {!status?.awaiting_owner && !otp && <GoogleIdentity linkedHint={status?.google_linked_hint} onUnlocked={(r) => finishUnlock(r.token, r.member)} />}
        {(status?.admin_email_login || status?.admin_passkey_login) && !otp && !adminEmail && !passkey && !forgot && (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            {status?.admin_passkey_login && <button type="button" data-testid="admin-passkey-login-btn" onClick={adminPasskey} disabled={adminPasskeyBusy} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary hover:underline disabled:opacity-50"><Fingerprint className="h-3.5 w-3.5" /> Administrator? Sign in with Face ID / passkey</button>}
            {status?.admin_email_login && <button type="button" data-testid="admin-email-login-link" onClick={() => setAdminEmail(true)} className="text-xs text-muted-foreground hover:text-primary hover:underline">Administrator? Sign in with an emailed code</button>}
          </div>
        )}
        {isNativeApp() && !status?.household_slug && <div className="mt-6"><a href="/h/reviewer-demo?next=/vault" data-testid="lock-app-review-link" className="text-[11px] text-muted-foreground hover:text-primary hover:underline">App Review access</a></div>}
        <a href="/" data-testid="lock-home-link" className="mt-8 inline-block text-xs text-muted-foreground hover:text-primary hover:underline">{tr("← Back to the HearthVault home page")}</a>
        {status?.household_slug && (
          <div className="mt-2"><a href="/h/primary?next=/welcome" data-testid="lock-leave-household-link" className="text-[11px] text-muted-foreground hover:text-primary hover:underline">{tr("Not your vault")}{status.household_name ? ` (${status.household_name})` : ""}? Switch or create your own</a></div>
        )}
      </motion.div>
    </div>
  );
};
